import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  provisionFromProduct,
  type ProvisionFromProductRequest,
  type ProvisionFromProductResponse,
} from '@/lib/api';
import {
  lifecycleError,
  refreshRepositoryLifecycle,
  repositoryLink,
  singleFlight,
  validRelease,
} from './repository-lifecycle-ui';

export interface ProvisionFields {
  customerOrganization: string;
  projectCode: string;
  projectName: string;
  productSourceId: string;
  releaseRef: string;
  releaseSha: string;
  extensionPaths: string;
}
const emptyFields: ProvisionFields = {
  customerOrganization: '',
  projectCode: '',
  projectName: '',
  productSourceId: '',
  releaseRef: 'refs/tags/',
  releaseSha: '',
  extensionPaths: '',
};
const labels: Record<keyof ProvisionFields, string> = {
  customerOrganization: 'Customer organization slug',
  projectCode: 'Project code',
  projectName: 'Project name',
  productSourceId: 'Approved product source ID',
  releaseRef: 'Approved release ref',
  releaseSha: 'Exact release SHA',
  extensionPaths: 'Extension paths (comma separated, optional)',
};

export function buildProvisionRequest(
  fields: ProvisionFields,
  approved: boolean,
  key: string
): ProvisionFromProductRequest | null {
  const name = /^[a-z0-9][a-z0-9-]{0,62}$/;
  const paths = fields.extensionPaths
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);
  if (
    !approved ||
    !name.test(fields.customerOrganization) ||
    !name.test(fields.projectCode) ||
    !fields.projectName.trim() ||
    fields.projectName.length > 200 ||
    !fields.productSourceId.trim() ||
    fields.productSourceId.length > 200 ||
    !validRelease(fields.releaseRef, fields.releaseSha) ||
    paths.length > 100 ||
    paths.some(
      p =>
        p.length > 500 ||
        !/^[A-Za-z0-9_.-]+(?:\/[A-Za-z0-9_.-]+)*$/.test(p) ||
        p.split('/').some(part => part === '..' || part === '.')
    )
  )
    return null;
  return {
    ...fields,
    projectName: fields.projectName.trim(),
    extensionPaths: paths,
    configEntries: {},
    approvalConfirmed: true,
    idempotencyKey: key,
  };
}

export function ProvisionForm({
  fields,
  approved,
  pending,
  canProvision,
  onChange,
  onApproval,
  onSubmit,
}: {
  fields: ProvisionFields;
  approved: boolean;
  pending: boolean;
  canProvision: boolean;
  onChange: (fields: ProvisionFields) => void;
  onApproval: (approved: boolean) => void;
  onSubmit: () => void;
}): React.ReactElement {
  return (
    <form
      className="space-y-3"
      onSubmit={event => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <p>
        Private code and configuration repositories will be created or reconciled in the customer
        organization. Full product history, a scoped bot, registration, project links, and webhooks
        are included.
      </p>
      <p>
        Existing URL registration is a separate action. Repository removal and default-branch
        changes are not part of provisioning. Deployment, delivery, and activation require separate
        approval.
      </p>
      <fieldset disabled={pending || !canProvision} className="space-y-2">
        {(Object.keys(labels) as (keyof ProvisionFields)[]).map(key => (
          <label key={key} className="block text-sm">
            {labels[key]}
            <Input
              value={fields[key]}
              name={key}
              autoComplete="off"
              onChange={event => {
                onChange({ ...fields, [key]: event.target.value });
              }}
            />
          </label>
        ))}
        <label className="flex gap-2 text-sm">
          <input
            type="checkbox"
            checked={approved}
            onChange={event => {
              onApproval(event.target.checked);
            }}
          />
          I approve these remote effects for the exact source and release above.
        </label>
      </fieldset>
      {!canProvision && (
        <p role="alert">An authenticated organization administrator is required.</p>
      )}
      <Button
        type="submit"
        disabled={
          pending || !canProvision || !buildProvisionRequest(fields, approved, 'validation')
        }
      >
        {pending ? 'Provisioning…' : 'Create customer repositories'}
      </Button>
    </form>
  );
}

export function ProvisionReceipt({
  result,
}: {
  result: ProvisionFromProductResponse;
}): React.ReactElement {
  const receipt = result.receipt;
  return (
    <section aria-label="Provisioning receipt" className="space-y-2 break-all" role="status">
      <h3>{result.replayed ? 'Replayed receipt' : 'Provisioning receipt'}</h3>
      <p>
        Run: {result.runId} · Project: {receipt.projectId}
      </p>
      <p>
        Release: {receipt.releaseRef} · <code>{receipt.releaseSha}</code>
      </p>
      {(
        [
          ['Customer code', receipt.downstreamRepositoryUrl],
          ['Configuration', receipt.configRepositoryUrl],
        ] as const
      ).map(([label, url]) => (
        <p key={label}>
          {label}:{' '}
          {repositoryLink(url) ? (
            <a href={url} rel="noreferrer" target="_blank" className="underline">
              {url}
            </a>
          ) : (
            'Repository URL unavailable'
          )}
        </p>
      ))}
      <ul>
        {receipt.effects.map((effect, i) => (
          <li key={i}>
            {effect.step}: {effect.effect}
          </li>
        ))}
      </ul>
      <p>
        Receipt SHA-256: <code>{result.receiptHash}</code>
      </p>
      <p>
        Next: inspect the project lineage. Deployment, delivery, and activation remain separate
        approvals.
      </p>
    </section>
  );
}

export function ProvisionFromProductDialog({
  open,
  onOpenChange,
  canProvision,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canProvision: boolean;
}): React.ReactElement {
  const client = useQueryClient();
  const [fields, setFields] = useState(emptyFields);
  const [approved, setApproved] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProvisionFromProductResponse | null>(null);
  const request = useRef<ProvisionFromProductRequest | null>(null);
  const submit = useRef(singleFlight());
  const run = (): void => {
    void submit.current(async () => {
      if (!canProvision) return;
      const input = buildProvisionRequest(
        fields,
        approved,
        request.current?.idempotencyKey ?? crypto.randomUUID()
      );
      if (!input) return;
      request.current = input;
      setPending(true);
      setError(null);
      try {
        setResult(await provisionFromProduct(input));
      } catch (failure) {
        setError(lifecycleError(failure));
      } finally {
        await refreshRepositoryLifecycle(client);
        setPending(false);
      }
    });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={value => {
        if (!pending) {
          onOpenChange(value);
          setApproved(false);
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create customer repositories</DialogTitle>
          <DialogDescription>
            Provision from an approved product release. Credentials are managed by the server.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <ProvisionReceipt result={result} />
        ) : (
          <ProvisionForm
            fields={fields}
            approved={approved}
            pending={pending}
            canProvision={canProvision}
            onChange={value => {
              setFields(value);
              setApproved(false);
              request.current = null;
              setError(null);
            }}
            onApproval={setApproved}
            onSubmit={run}
          />
        )}
        {error && (
          <div role="alert">
            <p>{error}</p>
            <p>Run request: {request.current?.idempotencyKey}. Keep fields unchanged for retry.</p>
            <Button disabled={pending || !approved || !canProvision} onClick={run}>
              Retry unchanged request
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
